// ─── Confirmation des actions Tier 2 avec fenêtre d'annulation (Story 7.1) ───
// Pattern Gmail (architecture : « undo window 5s avant exécution réelle ») :
// « Confirmer » lance un compte à rebours de 5 s AVANT l'appel
// `POST /api/chat/:id/actions/:actionId/confirm`. « Annuler (n s) » pendant la
// fenêtre → carte `cancelled`, rien n'est exécuté, l'action serveur est annulée.
// Sécurité par défaut : démontage du composant ou changement de conversation
// pendant la fenêtre = annulation (rien ne s'exécute hors de la vue).
// Action inconnue du serveur (404 : TTL dépassé ou redémarrage, actions en
// mémoire — PO Q1) ou expirée (410) → carte `expired` (« redemandez au chat »),
// jamais `failed`.

import { useCallback, useEffect, useRef } from 'react'
import { apiCancelChatAction, apiConfirmChatAction, isApiError } from '@/lib/api'
import {
  ACTION_EXPIRED_MESSAGE,
  isActionNavigateLink,
  type ActionCardData,
  type ActionCardState,
  type ActionNavigateLink,
} from '@/lib/chatTypes'

export const UNDO_WINDOW_SECONDS = 5

const GENERIC_CONFIRM_ERROR = "L'action n'a pas pu être exécutée, réessayez"

export type SetActionCardState = (
  messageId: string,
  cardId: string,
  state: ActionCardState,
  patch?: Partial<ActionCardData>
) => void

export type UseActionConfirmationOptions = {
  conversationId: string | null
  setActionCardState: SetActionCardState
}

type Countdown = {
  timer: ReturnType<typeof setInterval>
  messageId: string
  conversationId: string
  actionId: string
}

function resultTextOf(data: unknown): string | undefined {
  if (typeof data === 'object' && data !== null && 'resultText' in data) {
    const text = (data as { resultText?: unknown }).resultText
    if (typeof text === 'string') return text
  }
  return undefined
}

function navigateOf(data: unknown): ActionNavigateLink | undefined {
  if (typeof data !== 'object' || data === null) return undefined
  const nav = (data as { navigate?: unknown }).navigate
  return isActionNavigateLink(nav) ? nav : undefined
}

function isGoneError(e: unknown): boolean {
  return isApiError(e) && (e.status === 404 || e.status === 410)
}

export function useActionConfirmation({ conversationId, setActionCardState }: UseActionConfirmationOptions) {
  const countdowns = useRef(new Map<string, Countdown>())
  const setStateRef = useRef(setActionCardState)
  setStateRef.current = setActionCardState

  // Annulation serveur best-effort. Si le serveur ne connaît plus l'action
  // (404/410), la carte passe `expired` ; `reportGone: false` au démontage
  // (la vue n'est plus là, rien à afficher).
  const sendCancel = useCallback(
    async (messageId: string, cardId: string, convId: string, actionId: string, reportGone = true) => {
      try {
        await apiCancelChatAction(convId, actionId)
      } catch (e: unknown) {
        if (reportGone && isGoneError(e)) {
          setStateRef.current(messageId, cardId, 'expired', { errorText: ACTION_EXPIRED_MESSAGE })
        }
        // Autres erreurs : l'action expirera de toute façon côté serveur.
      }
    },
    []
  )

  const execute = useCallback(async (messageId: string, cardId: string, convId: string, actionId: string) => {
    const set = setStateRef.current
    try {
      const res = await apiConfirmChatAction(convId, actionId)
      if (res.state === 'executed') {
        set(messageId, cardId, 'executed', {
          resultText: resultTextOf(res.result.data) ?? 'Action exécutée',
          errorText: undefined,
          navigate: navigateOf(res.result.data),
        })
      } else {
        set(messageId, cardId, 'failed', { errorText: res.result.error ?? GENERIC_CONFIRM_ERROR })
      }
    } catch (e: unknown) {
      const message = e instanceof Error && e.message ? e.message : GENERIC_CONFIRM_ERROR
      if (isGoneError(e)) {
        set(messageId, cardId, 'expired', { errorText: ACTION_EXPIRED_MESSAGE })
      } else if (isApiError(e) && e.status === 409) {
        set(messageId, cardId, 'failed', { errorText: message })
      } else {
        // 429 (budget emails) ou réseau : l'action reste confirmable
        // (le confirm serveur est idempotent, un rejeu ne ré-exécute jamais).
        set(messageId, cardId, 'proposed', { errorText: message })
      }
    }
  }, [])

  const confirm = useCallback(
    (messageId: string, card: ActionCardData) => {
      if (!conversationId || !card.actionId || countdowns.current.has(card.id)) return
      const convId = conversationId
      const actionId = card.actionId
      let left = UNDO_WINDOW_SECONDS
      setStateRef.current(messageId, card.id, 'confirmed', { undoSecondsLeft: left, errorText: undefined })
      const timer = setInterval(() => {
        left -= 1
        if (left > 0) {
          setStateRef.current(messageId, card.id, 'confirmed', { undoSecondsLeft: left })
          return
        }
        clearInterval(timer)
        countdowns.current.delete(card.id)
        setStateRef.current(messageId, card.id, 'confirmed', { undoSecondsLeft: undefined })
        void execute(messageId, card.id, convId, actionId)
      }, 1000)
      countdowns.current.set(card.id, { timer, messageId, conversationId: convId, actionId })
    },
    [conversationId, execute]
  )

  // Annulation pendant la fenêtre de 5 s : rien n'a été envoyé au serveur.
  const undo = useCallback(
    (messageId: string, card: ActionCardData) => {
      const pending = countdowns.current.get(card.id)
      if (!pending) return
      clearInterval(pending.timer)
      countdowns.current.delete(card.id)
      setStateRef.current(messageId, card.id, 'cancelled', { undoSecondsLeft: undefined })
      void sendCancel(messageId, card.id, pending.conversationId, pending.actionId)
    },
    [sendCancel]
  )

  // « Annuler » sur une carte encore `proposed` : annulation locale + serveur.
  const cancel = useCallback(
    (messageId: string, card: ActionCardData) => {
      if (countdowns.current.has(card.id)) return undo(messageId, card)
      setStateRef.current(messageId, card.id, 'cancelled', { errorText: undefined })
      if (conversationId && card.actionId) void sendCancel(messageId, card.id, conversationId, card.actionId)
    },
    [conversationId, undo, sendCancel]
  )

  // Démontage / changement de conversation : toute fenêtre en cours est annulée.
  useEffect(() => {
    const map = countdowns.current
    return () => {
      for (const [cardId, pending] of map) {
        clearInterval(pending.timer)
        setStateRef.current(pending.messageId, cardId, 'cancelled', { undoSecondsLeft: undefined })
        void sendCancel(pending.messageId, cardId, pending.conversationId, pending.actionId, false)
      }
      map.clear()
    }
  }, [conversationId, sendCancel])

  return { confirm, undo, cancel }
}
